import * as React from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import {
	FilterPopover,
	useFilterContext,
} from "../../components/filter-button";
import { BILLING_CYCLE_NAME } from "../partner-templates/components/subscription-catalog";
import type { BillingCycle, PlanTier } from "../partner-templates/types";

interface MultiSelectFilterPopoverProps<T extends string> {
	title: string;
	options: { value: T; label: string }[];
}

export function PlanFilterPopover(): React.JSX.Element {
	return (
		<MultiSelectFilterPopover<PlanTier>
			title="Filter by plan"
			options={[
				{ value: "BUSINESS", label: "Business" },
				{ value: "ENTERPRISE", label: "Enterprise" },
				{ value: "AGENCY", label: "Agency" },
			]}
		/>
	);
}

const CYCLE_FILTER_OPTIONS: { value: BillingCycle; label: string }[] = (
	[
		"MONTHLY",
		"ANNUAL",
		"TWO_YEARS",
		"THREE_YEARS",
		"FOUR_YEARS",
		"FIVE_YEARS",
	] as const
).map((value) => ({ value, label: BILLING_CYCLE_NAME[value] }));

export function CycleFilterPopover(): React.JSX.Element {
	return (
		<MultiSelectFilterPopover<BillingCycle>
			title="Filter by billing cycle"
			options={CYCLE_FILTER_OPTIONS}
		/>
	);
}

function MultiSelectFilterPopover<T extends string>({
	title,
	options,
}: MultiSelectFilterPopoverProps<T>): React.JSX.Element {
	const {
		anchorEl,
		onApply,
		onClose,
		open,
		value: initialValue,
	} = useFilterContext();
	const [selected, setSelected] = React.useState<T[]>([]);

	React.useEffect(() => {
		setSelected((initialValue as T[] | undefined) ?? []);
	}, [initialValue]);

	const toggle = (value: T) => {
		setSelected((prev) =>
			prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
		);
	};

	return (
		<FilterPopover
			anchorEl={anchorEl}
			onClose={onClose}
			open={open}
			title={title}>
			<Stack>
				{options.map((opt) => (
					<FormControlLabel
						key={opt.value}
						control={
							<Checkbox
								checked={selected.includes(opt.value)}
								onChange={() => toggle(opt.value)}
							/>
						}
						label={opt.label}
					/>
				))}
			</Stack>
			<Button
				onClick={() => onApply(selected)}
				variant="contained">
				Apply
			</Button>
		</FilterPopover>
	);
}
